import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactSVG } from "react-svg";

import { formatCurrency, transformDateToLocalDateString } from "helpers";
import { getUsageOverviewAPI } from "services/api/finance/usage-overview/api";
import { BILLING_PERIOD_LABELS } from "services/api/finance/usage-overview/consts";
import { UsageOverview } from "services/api/finance/usage-overview/types";
import { Routes as FinanceRoutes } from "ui/modules/finance/navigation";

/**
 * Paket i potrošnja tvrtke, stalno vidljivi u gornjoj traci.
 *
 * Vidi ga samo korisnik s pravom na potrošnju: poslužitelj ostalima odbije poziv, a pokazatelj
 * se tada ne prikaže. Aplikacija nema provjeru prava na frontendu, pa je poslužitelj jedini izvor.
 *
 * Panel slijedi izbornik računa pokraj njega — iste boje, razmaci i stavke — da dva izbornika u
 * traci izgledaju kao dio iste cjeline.
 */
const UsageIndicator: React.FC = () => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const [overview, setOverview] = useState<UsageOverview | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const fetchOverview = useCallback(async () => {
    try {
      setOverview(await getUsageOverviewAPI());
    } catch {
      // Bez prava ili bez obračuna: pokazatelj ostaje skriven, bez poruke.
      setOverview(null);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Otvaranje osvježava brojke: traka stoji otvorena satima, a potrošnja raste.
  const handleToggle = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) fetchOverview();
      return !prev;
    });
  }, [fetchOverview]);

  const handleShowAll = useCallback(() => {
    setIsOpen(false);
    navigate(FinanceRoutes.UsageSearchPageRouter);
  }, [navigate]);

  if (!overview) return null;

  const { subscription, usage, usageTotal, usageSince } = overview;

  const packsLabel = subscription?.packs.length ? subscription.packs.join(" + ") : "Potrošnja";

  const isTrialActive =
    !!subscription?.trialEndsOn && new Date(subscription.trialEndsOn) > new Date();

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={handleToggle}
        title="Paket i potrošnja ovog mjeseca"
        aria-expanded={isOpen}
        aria-haspopup="true"
        className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/20 focus:outline-none"
      >
        <ReactSVG src="/media/icons/calculator.svg" className="w-4 h-4 stroke-white" />
        <span className="hidden md:inline">{packsLabel}:</span>

        {/* Iznos je razlog zbog kojeg je pokazatelj u traci, pa se izdvaja od naziva paketa. */}
        <span className="tabular-nums font-bold text-emerald-400">
          {formatCurrency.format(usageTotal)}
        </span>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 z-20 mt-2 w-80 origin-top-right rounded-lg bg-[#1f2023] text-white shadow-2xl ring-1 ring-black/20 overflow-hidden"
          role="menu"
          aria-orientation="vertical"
        >
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Paket</p>

            {subscription ? (
              <>
                <p className="text-sm font-semibold mt-1">{packsLabel}</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {BILLING_PERIOD_LABELS[subscription.billingPeriod] ?? subscription.billingPeriod}
                  {" · "}
                  {subscription.vehicleCount} vozila
                </p>
                {isTrialActive && (
                  <p className="text-xs text-emerald-400 mt-1">
                    Probni rok do{" "}
                    {transformDateToLocalDateString(new Date(subscription.trialEndsOn!))}
                  </p>
                )}
              </>
            ) : (
              // Obavijest, ne sporedna oznaka: mora se čitati jednako lako kao sadržaj.
              <p className="text-sm text-slate-200 mt-1">Tvrtka nema ugovorenu pretplatu.</p>
            )}
          </div>

          {subscription && subscription.monthAmount !== null && (
            <div className="px-4 py-3 border-b border-white/10">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-200">Pretplata ovaj mjesec</p>
                <p className="text-sm font-semibold tabular-nums">
                  {formatCurrency.format(subscription.monthAmount)}
                </p>
              </div>
              {subscription.isMinimumApplied && (
                <p className="text-xs text-slate-400 mt-1">
                  Naplaćuje se minimalna naknada.
                </p>
              )}
            </div>
          )}

          <div className="px-4 py-3">
            <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
              Potrošnja od {transformDateToLocalDateString(new Date(usageSince))}
            </p>

            {usage.length === 0 ? (
              <p className="text-sm text-slate-200 mt-2">
                Ovaj mjesec još nema naplaćene potrošnje.
              </p>
            ) : (
              <div className="mt-2 space-y-2">
                {usage.map((item) => (
                  <div key={item.keyCode} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-slate-200 truncate">
                      {item.name}
                      <span className="text-slate-400"> × {item.count}</span>
                    </span>
                    <span className="tabular-nums whitespace-nowrap">
                      {formatCurrency.format(item.amount)}
                    </span>
                  </div>
                ))}

                <div className="flex items-center justify-between text-sm font-semibold border-t border-white/10 pt-2">
                  <span>Ukupno potrošnja</span>
                  <span className="tabular-nums">{formatCurrency.format(usageTotal)}</span>
                </div>
              </div>
            )}
          </div>

          {/*
            Stavka izbornika kao "Odjava" u izborniku računa, samo zelena umjesto crvene. Strelica
            stoji desno, jer ondje kaže da stavka vodi na drugi ekran; deblji potez da se vidi.
          */}
          <div className="py-1 border-t border-white/10">
            <button
              type="button"
              role="menuitem"
              onClick={handleShowAll}
              className="flex items-center justify-between gap-3 w-full text-left px-4 py-3 text-sm font-medium text-emerald-400 hover:bg-white/5"
            >
              Izračuni i potrošnja
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(UsageIndicator);
